import React, { useMemo } from "react";
import {
  useGetAdminCardCatalogQuery,
  useGetAdminCardScheduleQuery,
  useGetAdminCardScheduleDayQuery,
} from "../../api/respectCardsApi";

/**
 * Phase 2b cards-launch — Overview tab.
 * Aggregates catalog + 14-day schedule into a glanceable dashboard.
 */
function todayUtcKey(): string {
  const now = new Date();
  const yyyy = now.getUTCFullYear();
  const mm = String(now.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(now.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const tile: React.CSSProperties = {
  border: "1px solid #ece6f7",
  borderRadius: 8,
  background: "#faf7ff",
  padding: 12,
  minHeight: 92,
};

const kicker: React.CSSProperties = {
  fontSize: "0.72rem",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#7a6a9c",
  marginBottom: 4,
};

const value: React.CSSProperties = {
  fontSize: "1.4rem",
  fontWeight: 700,
  color: "#222",
};

const tierChip: React.CSSProperties = {
  display: "inline-block",
  fontSize: "0.7rem",
  fontWeight: 600,
  padding: "2px 8px",
  borderRadius: 999,
  marginRight: 6,
  marginTop: 4,
  background: "rgba(99, 102, 241, 0.15)",
  color: "#3730a3",
  textTransform: "uppercase",
};

const OverviewTab: React.FC = () => {
  const today = useMemo(() => todayUtcKey(), []);
  const { data: catalogData, isLoading: catalogLoading, error: catalogError } =
    useGetAdminCardCatalogQuery();
  const { data: scheduleData, isLoading: scheduleLoading, error: scheduleError } =
    useGetAdminCardScheduleQuery();
  const { data: dayData, isLoading: dayLoading, error: dayError } =
    useGetAdminCardScheduleDayQuery(today);

  if (catalogLoading || scheduleLoading || dayLoading) {
    return <p style={{ color: "#666" }}>loading overview…</p>;
  }

  if (catalogError || scheduleError || dayError) {
    return (
      <p style={{ color: "#a00" }}>
        failed to load overview data. check the network tab and try again.
      </p>
    );
  }

  const cards = catalogData?.cards ?? [];
  const activeCount = cards.filter(
    (c) => (c as { isActive?: boolean; status?: string }).isActive !== false &&
      (c as { status?: string }).status !== "inactive",
  ).length;

  const schedules = scheduleData?.schedules ?? [];
  const todayRow = schedules.find((s) => s.date === today);

  const drop = dayData?.drop ?? null;
  const totalClaims = drop?.totalClaims ?? 0;
  const missCount = (drop?.claims ?? []).filter((c) => c.outcome === "nothing").length;

  return (
    <section
      style={{
        background: "#fff",
        padding: 16,
        borderRadius: 8,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
        marginBottom: 16,
      }}
    >
      <h2 style={{ fontSize: "1.1rem", color: "#333", marginBottom: 12 }}>
        Overview
      </h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 12,
        }}
      >
        <div style={tile}>
          <div style={kicker}>Active cards</div>
          <div style={value}>{activeCount}</div>
          <div style={{ fontSize: "0.78rem", color: "#666" }}>
            of {cards.length} total in catalog
          </div>
        </div>
        <div style={tile}>
          <div style={kicker}>Scheduled days</div>
          <div style={value}>{schedules.length}</div>
          <div style={{ fontSize: "0.78rem", color: "#666" }}>days in horizon</div>
        </div>
        <div style={tile}>
          <div style={kicker}>Today’s tiers ({today})</div>
          {todayRow && todayRow.slots.length ? (
            <div>
              {todayRow.slots.map((slot, idx) => (
                <span key={idx} style={tierChip}>
                  {slot.rarity}
                </span>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: "0.82rem", color: "#666" }}>
              no schedule for today
            </div>
          )}
        </div>
        <div style={tile}>
          <div style={kicker}>Today’s pulls</div>
          <div style={value}>{totalClaims}</div>
          <div style={{ fontSize: "0.78rem", color: "#666" }}>
            {missCount} miss{missCount === 1 ? "" : "es"}
          </div>
        </div>
      </div>
    </section>
  );
};

export default OverviewTab;
